import endpoints from './endpoints.json';
import { apiUrl, avatarType, usuario } from '../../constants';
import { AvatarConfiguration, AvatarResponse } from '../../models/avatar';
import { User } from '../../models/auth';
//import { toMessageFormat } from '../../models/chat';

// Declaro la variable 'process' para evitar errores de tipado en entornos sin definiciones de Node
declare var process: any;

const INTERVIEW_VOICE_ID = 'es-ES-ElviraNeural';
const INTERVIEW_SPEECH_LANGUAGE = 'es-ES';
const isInterviewAvatar = avatarType?.toLowerCase().startsWith('ent_');

const withInterviewSpeechConfig = (
  config: AvatarConfiguration
): AvatarConfiguration => {
  if (!isInterviewAvatar) {
    return config;
  }

  return {
    ...config,
    azureVoiceId: INTERVIEW_VOICE_ID,
    speechRecognitionLanguage: INTERVIEW_SPEECH_LANGUAGE,
  };
};

// Función para obtener el avatar del usuario
const getUserAvatar = async (userToken: string, userEmail: string) => {
  try {
    // Log de la URL generada
    const avatarUrl = `${apiUrl}${endpoints.get.userAvatar}?email=${userEmail}`;
    //console.log('URL generada para el avatar:', avatarUrl);  // Log para verificar la URL

    const response = await fetch(avatarUrl, {
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`Error al obtener avatar: ${response.status}`);
    }

    const data = await response.json();
    //console.log('Datos del avatar obtenidos:', data);  // Log para verificar los datos de la respuesta
    return data.avatarUrl;  // Asegúrate de que la API devuelva la URL correcta
  } catch (error) {
    console.error('Error obteniendo avatar:', error);
    return null;
  }
};

const getGptData = async (
  userMessage: string,
  userToken: string,
  user: User,
  dominio?: string | null,
  environment: 'des' | 'prod' = 'prod',
  urlDominio?: string | null
): Promise<AvatarResponse & { tags?: string[]; tagFrases?: { [key: string]: string[] } } | undefined> => {
  try {

    if (dominio) {
      userMessage = `${userMessage} *${dominio}*`;
    }

    // Seleccionar URL base y code según entorno
    const baseUrl =
      environment === 'des'
        ? (process.env.REACT_APP_GPT_CEU_API_DES || process.env.REACT_APP_GPT_CEU_API_ES)
        : process.env.REACT_APP_GPT_CEU_API_ES;

    const gptCode =
      environment === 'des'
        ? (process.env.REACT_APP_GPT_CODE_DES || process.env.REACT_APP_GPT_CODE_ES)
        : process.env.REACT_APP_GPT_CODE_ES;
    const gptPath = '/GPT_CEU';

    const decodedCode = decodeURIComponent(gptCode ?? '');

    const params = new URLSearchParams({
      code: decodedCode,
      session_id: user?.email ?? '',
      mensaje: userMessage,
      usuario: user?.name ?? '',
    });
    if (urlDominio) {
      params.set('dominio', urlDominio);
    }

    const fullUrl = `${baseUrl}${gptPath}?${params.toString()}`;

    const response = await fetch(fullUrl, {
      headers: {
        Authorization: `Bearer ${userToken}`,
        'Content-Type': 'application/json',
      },
    });

    // 203 = el backend no recibió session_id; recargar para reestablecer la sesión
    if (response.status === 203) {
      window.location.reload();
      return;
    }

    if (!response.ok) {
      throw new Error(`Error al obtener datos: ${response.status}`);
    }

    const dataResponse: AvatarResponse = await response.json();

    // EXTRAER LOS TAGS
    const tags = dataResponse?.respuesta_corta?.tags?.map(tagObj => tagObj.Tag) || [];

    // EXTRAER LAS FRASES DE CADA TAG
    const tagFrases = dataResponse?.respuesta_corta?.tags?.reduce((acc, tagObj) => {
      acc[tagObj.Tag] = tagObj.frases || [];
      return acc;
    }, {} as { [key: string]: string[] });

    // Devolver la respuesta original junto con los tags y frases
    return {
      ...dataResponse,
      tags,
      tagFrases,
    };
  } catch (error) {
    console.error('Error obteniendo datos GPT:', error);
  }
};


// Envía la valoración (1-5) del asistente. El backend solo la acepta si tenía una encuesta
// pendiente para este usuario y la guarda de forma anónima; no se añade al chat.
const sendSatisfactionRating = async (
  puntuacion: number,
  userToken: string,
  user: User,
  environment: 'des' | 'prod' = 'prod'
): Promise<boolean> => {
  try {
    const baseUrl =
      environment === 'des'
        ? (process.env.REACT_APP_GPT_CEU_API_DES || process.env.REACT_APP_GPT_CEU_API_ES)
        : process.env.REACT_APP_GPT_CEU_API_ES;
    const gptCode =
      environment === 'des'
        ? (process.env.REACT_APP_GPT_CODE_DES || process.env.REACT_APP_GPT_CODE_ES)
        : process.env.REACT_APP_GPT_CODE_ES;

    const params = new URLSearchParams({
      code: decodeURIComponent(gptCode ?? ''),
      session_id: user?.email ?? '',
      mensaje: `--valoracion:${puntuacion}`,
      usuario: user?.name ?? '',
    });

    const response = await fetch(`${baseUrl}/GPT_CEU?${params.toString()}`, {
      headers: {
        Authorization: `Bearer ${userToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) {
      throw new Error(`Error enviando valoración: ${response.status}`);
    }
    const data = await response.json();
    return Boolean(data?.ok);
  } catch (error) {
    console.error('Error enviando valoración del asistente:', error);
    return false;
  }
};

const getVariableData = async (
  userMessage: string,
  streamId: string
): Promise<string | undefined> => {
  try {
    const params = new URLSearchParams({
      code: decodeURIComponent(process.env.REACT_APP_CONFIG_CODE ?? ''),
      session_id: streamId,
      mensaje: userMessage,
      usuario: usuario ?? '',
      avatar: avatarType,
    });
    const response = await fetch(
      `${process.env.REACT_APP_CEU_API}${endpoints.get.customData}?${params.toString()}`
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.text();
    return data;
  } catch (error) {
    console.error('Error obteniendo respuesta del avatar:', error);
  }
};

type AvatarFeedbackPayload = {
  session_id: string;
  avatar: string;
  usuario?: string | null;
  email?: string;
  tipo: 'simulamnesis' | 'entrevista';
};

const sendAvatarFeedback = async (
  payload: AvatarFeedbackPayload
): Promise<{ ok: boolean; message?: string }> => {
  try {
    const feedbackCode =
      process.env.REACT_APP_AVATAR_FEEDBACK_CODE ||
      process.env.REACT_APP_CONFIG_CODE;

    const response = await fetch(
      `${process.env.REACT_APP_CEU_API}${endpoints.post.avatarFeedback}?code=${feedbackCode}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      }
    );

    const responseText = await response.text();
    let parsedResponse: any = null;
    try {
      parsedResponse = responseText ? JSON.parse(responseText) : null;
    } catch {
      parsedResponse = null;
    }

    if (!response.ok) {
      throw new Error(
        parsedResponse?.message ||
        responseText ||
        `Error solicitando feedback: ${response.status}`
      );
    }

    return {
      ok: true,
      message: parsedResponse?.message || responseText || undefined,
    };
  } catch (error) {
    console.error('Error solicitando feedback del avatar:', error);
    return {
      ok: false,
      message: error instanceof Error ? error.message : 'Error solicitando feedback',
    };
  }
};

const getInitialConfig = async (): Promise<AvatarConfiguration | undefined> => {
  try {
    if (avatarType.toLowerCase() === "gptceu") {
      const configStr = process.env.REACT_APP_GPT_CEU_CONFIG;
      if (configStr) {
        const config: AvatarConfiguration = JSON.parse(configStr);
        return withInterviewSpeechConfig(config);
      } else {
        console.error("No se encontró la configuración en REACT_APP_GPT_CEU_CONFIG");
        return undefined;
      }
    } else {
      const response = await fetch(
        `${process.env.REACT_APP_CEU_API}${endpoints.get.avatarConfiguration}?code=${process.env.REACT_APP_CONFIG_CODE}&avatar_type=${avatarType}`
      );
      if (response.ok && response.status === 200) {
        const jsonResponse: AvatarConfiguration = await response.json();
        if (jsonResponse) {
          return withInterviewSpeechConfig(jsonResponse);
        } else {
          console.error("Configuración inicial sin datos", jsonResponse);
        }
      }
    }
  } catch (error) {
    console.error(`Error con el avatar ${avatarType}`, error);
  }
};

const uploadFiles = async (
  files: File[],
  userEmail: string,
  userMessage: string,
  environment: 'des' | 'prod' = 'prod'
): Promise<any | false> => {
  try {
    const postData = new FormData();
    // Añadimos cada archivo usando la clave 'pdf'
    for (let i = 0; i < files.length; i++) {
      postData.append('pdf', files[i], files[i].name);
    }
    
    const isDev = environment === 'des';

    // En DES usamos SIEMPRE REACT_APP_UPLOAD_FILE_CODE_DES.
    // En PROD usamos solo REACT_APP_UPLOAD_FILE_CODE.
    const rawCode = isDev
      ? process.env.REACT_APP_UPLOAD_FILE_CODE_DES
      : process.env.REACT_APP_UPLOAD_FILE_CODE;

    if (!rawCode) {
      console.error(
        `Falta la variable de entorno ${
          isDev ? 'REACT_APP_UPLOAD_FILE_CODE_DES' : 'REACT_APP_UPLOAD_FILE_CODE'
        } para subida de archivos`
      );
      return false;
    }

    // Se asegura de que el código termine en "=="
    const finalCode = rawCode.endsWith('==') ? rawCode : rawCode + '==';
    
    // Se definen los parámetros de consulta según lo espera el backend
    const queryParams = new URLSearchParams({
      code: finalCode,
      user_mail: userEmail,
      mensaje: userMessage
    });

    // Base URL según entorno, usando las variables REACT_APP_GPT_CEU_API_ES / REACT_APP_GPT_CEU_API_DES
    const uploadBaseUrl =
      environment === 'des'
        ? (process.env.REACT_APP_GPT_CEU_API_DES || process.env.REACT_APP_GPT_CEU_API_ES) + endpoints.post.uploadFile
        : (process.env.REACT_APP_GPT_CEU_API_ES as string) + endpoints.post.uploadFile;

    const response = await fetch(`${uploadBaseUrl}?${queryParams.toString()}`, {
      method: 'POST',
      body: postData,
    });

    if (response.ok && response.status === 200) {
      const data = await response.json();
      return data ?? false;
    } else {
      console.error('Fallo al subir ficheros, status:', response.status);
      return false;
    }
  } catch (error) {
    console.error('Fallo al subir ficheros -->', error);
    return false;
  }
};

export {
  getInitialConfig,
  getGptData,
  getVariableData,
  sendAvatarFeedback,
  sendSatisfactionRating,
  uploadFiles,
  getUserAvatar,
};
